import React, { useMemo } from 'react';
import { DocumentationFile } from '../../../graphql/graphql';
import { getMomentDate } from '../../../Utils/dates.util';
import { useAppContext } from '../../../Services/AppContext';
import useTranslation from '../../../Hooks/useTranslation';
import { ECardType } from '../Enums/ECardType.enum';

interface Transfer {
	direction: string;
	text: string;
	date: string;
}

export default function useDocumentationFileTransfer(
	item: DocumentationFile,
	eCardType: ECardType,
) {
	const { dateFormatMoment } = useAppContext();
	const { t } = useTranslation();

	const texts: { from: string; to: string } | undefined = useMemo(() => {
		switch (eCardType) {
			case ECardType.HEALTH_DOCUMENTATION:
				return {
					to: 'component.fileTransfers.toPatientECard',
					from: 'component.fileTransfers.fromPatientECard',
				};
			case ECardType.PATIENT_E_CARD:
				return {
					to: 'component.fileTransfers.toHealthDocumentation',
					from: 'component.fileTransfers.formHealthDocumentation',
				};
		}
		return undefined;
	}, [eCardType]);

	const transfersRecords: Transfer[] = useMemo(() => {
		const temp: Transfer[] = [];
		const transferData = item.fileTransfers?.value?.transfers;

		for (const transfer of transferData || []) {
			if (transfer?.originalFile?.id) {
				temp.push({
					direction: `${t('component.fileTransfers.moved')} ${t(texts?.from)}`,
					text: `${getMomentDate(transfer.time).format(
						dateFormatMoment + ' HH:mm',
					)} ${transfer?.duplicatedBy?.name}`,
					date: getMomentDate(transfer.time).format(
						dateFormatMoment + ' HH:mm',
					),
				});
			} else {
				temp.push({
					direction: `${t('component.fileTransfers.moved')} ${t(texts?.to)}`,
					text: `${getMomentDate(transfer.time).format(
						dateFormatMoment + ' HH:mm',
					)} ${transfer?.duplicatedBy?.name}, ${t(
						'component.fileTransfers.folderName',
					)} : ${transfer?.movedToFolder?.name}, ${t(
						'component.fileTransfers.fileName',
					)} : ${transfer?.newName}`,
					date: getMomentDate(transfer.time).format(
						dateFormatMoment + ' HH:mm',
					),
				});
			}
		}

		return temp;
	}, [item]);

	const isNotMovedToHealthDocumentation: boolean = useMemo(() => {
		const transferData = item.fileTransfers?.value?.transfers;
		let temp = true;
		for (const transfer of transferData || []) {
			if (!transfer?.originalFile?.id) {
				temp = false;
			}
		}
		return temp;
	}, [item]);

	return { transfersRecords, isNotMovedToHealthDocumentation };
}
